import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { captureError } from '@/lib/sentry';

interface Props {
  children: ReactNode;
  /** Custom title for the error fallback UI */
  fallbackTitle?: string;
  /** Custom description for the error fallback UI */
  fallbackDescription?: string;
  /** Callback when user clicks retry (if not provided, reloads page) */
  onReset?: () => void;
  /** Dashboard name for logging purposes */
  dashboardName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Generic error boundary for dashboard components.
 * Catches JavaScript errors in child component tree and displays
 * a fallback UI instead of crashing the entire app.
 *
 * Usage:
 * ```tsx
 * <DashboardErrorBoundary
 *   fallbackTitle="Client Dashboard Error"
 *   dashboardName="ClientDashboard"
 * >
 *   <ClientDashboard {...props} />
 * </DashboardErrorBoundary>
 * ```
 */
export class DashboardErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { dashboardName = 'Dashboard' } = this.props;

    // Log to console with structured format
    console.error(`[${dashboardName}] Error caught by boundary:`, {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    this.setState({
      error,
      errorInfo,
    });

    // Send to Sentry with context
    captureError(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        dashboardName,
        source: 'DashboardErrorBoundary',
      },
    });
  }

  private handleReset = () => {
    const { onReset } = this.props;

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    if (onReset) {
      onReset();
    } else {
      // Default: reload the page
      window.location.reload();
    }
  };

  private handleGoBack = () => {
    window.history.back();
  };

  public render() {
    const {
      children,
      fallbackTitle = 'Something went wrong',
      fallbackDescription = 'This section encountered an unexpected error. Please try refreshing or contact support if the issue persists.',
    } = this.props;

    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-[400px] p-6">
          <Card className="max-w-2xl w-full">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center space-y-4">
                {/* Error Icon */}
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-950 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>

                {/* Error Message */}
                <div className="space-y-2">
                  <h2 className="text-2xl font-semibold">{fallbackTitle}</h2>
                  <p className="text-muted-foreground">{fallbackDescription}</p>
                </div>

                {/* Dev-only error details */}
                {import.meta.env.DEV && this.state.error && (
                  <details className="w-full text-left">
                    <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                      Error Details (Development Only)
                    </summary>
                    <pre className="mt-2 p-4 bg-muted rounded-md overflow-auto text-xs max-h-48">
                      <code>{this.state.error.toString()}</code>
                      {this.state.errorInfo && (
                        <code className="block mt-2 text-muted-foreground">{this.state.errorInfo.componentStack}</code>
                      )}
                    </pre>
                  </details>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button onClick={this.handleReset}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button variant="outline" onClick={this.handleGoBack}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Go Back
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return children;
  }
}

export default DashboardErrorBoundary;
